import React, { useState } from 'react';
import { testSampleData } from '../../utils/validateDataFormat';
import { testBackendConnectivity } from '../../utils/testBackend';
import Button from '../common/Button';

const RegistrationTest = () => {
  const [testResults, setTestResults] = useState(null);
  const [backendResults, setBackendResults] = useState(null);

  const runDataFormatTest = () => {
    console.log('🧪 Running data format test...');
    const result = testSampleData();
    setTestResults(result);
    console.log('📋 Test result:', result);
  };

  const runBackendTest = async () => {
    console.log('🌐 Running backend connectivity test...');
    try {
      const result = await testBackendConnectivity();
      setBackendResults(result);
      console.log('📡 Backend test result:', result);
    } catch (error) {
      console.error('❌ Backend test failed:', error);
      setBackendResults({ success: false, error: error.message });
    }
  };

  const testRegistrationFlow = () => {
    console.log('🚀 Testing registration data flow...');
    
    // Simulate form data
    const mockFormData = {
      '1': 'Test User',
      '2': 'test@example.com',
      '3': '+1234567890',
      '4': 'Test Company',
      '5': 'Developer'
    };

    const mockFields = [
      { id: '1', field_name: 'full_name', field_label: 'Full Name' },
      { id: '2', field_name: 'email', field_label: 'Email' },
      { id: '3', field_name: 'phone', field_label: 'Phone' },
      { id: '4', field_name: 'organization', field_label: 'Organization' },
      { id: '5', field_name: 'position', field_label: 'Position' }
    ];

    console.log('📝 Mock form data:', mockFormData);
    console.log('📋 Mock fields:', mockFields);

    // Test the validation function
    const { validateAttendeeDataFormat } = require('../../utils/validateDataFormat');
    const validation = validateAttendeeDataFormat(mockFormData, mockFields);
    
    console.log('✅ Validation result:', validation);
    console.log('📤 Data that would be sent to Laravel:', validation.data);
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-lg border max-w-4xl mx-auto">
      <h2 className="text-2xl font-bold mb-6 text-gray-800">🧪 Registration Testing Dashboard</h2>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Button onClick={runDataFormatTest} className="w-full">
          Test Data Format
        </Button>
        <Button onClick={runBackendTest} className="w-full">
          Test Backend
        </Button>
        <Button onClick={testRegistrationFlow} className="w-full">
          Test Registration Flow
        </Button>
      </div>

      {/* Data Format Test Results */}
      {testResults && (
        <div className="mb-6">
          <h3 className="text-lg font-semibold mb-3">📋 Data Format Test Results</h3>
          <div className={`p-4 rounded-lg ${testResults.isValid ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
            <p className={`font-semibold ${testResults.isValid ? 'text-green-800' : 'text-red-800'}`}>
              {testResults.isValid ? '✅ Data format is valid!' : '❌ Data format has errors'}
            </p>
            {testResults.errors.length > 0 && (
              <ul className="mt-2 text-red-600">
                {testResults.errors.map((error, index) => (
                  <li key={index}>• {error}</li>
                ))}
              </ul>
            )}
            <details className="mt-3">
              <summary className="cursor-pointer text-sm text-gray-600">View formatted data</summary>
              <pre className="mt-2 text-xs bg-gray-100 p-2 rounded overflow-x-auto">
                {JSON.stringify(testResults.data, null, 2)}
              </pre>
            </details>
          </div>
        </div>
      )}

      {/* Backend Test Results */}
      {backendResults && (
        <div className="mb-6">
          <h3 className="text-lg font-semibold mb-3">🌐 Backend Test Results</h3>
          <div className={`p-4 rounded-lg ${backendResults.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
            <p className={`font-semibold ${backendResults.success ? 'text-green-800' : 'text-red-800'}`}>
              {backendResults.success ? '✅ Backend is accessible!' : '❌ Backend connection failed'}
            </p>
            {backendResults.tests && (
              <div className="mt-3">
                {backendResults.tests.map((test, index) => (
                  <div key={index} className="text-sm mb-1">
                    <span className={test.success ? 'text-green-600' : 'text-red-600'}>
                      {test.success ? '✅' : '❌'} {test.name}: {test.status}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="font-semibold text-blue-800 mb-2">📝 Instructions:</h4>
        <ol className="text-sm text-blue-700 space-y-1">
          <li>1. <strong>Test Data Format</strong> - Verify our data matches Laravel expectations</li>
          <li>2. <strong>Test Backend</strong> - Check if Laravel API is accessible</li>
          <li>3. <strong>Test Registration Flow</strong> - Simulate the complete registration process</li>
          <li>4. <strong>Check Console</strong> - Open browser DevTools to see detailed logs</li>
          <li>5. <strong>Try Real Registration</strong> - Go to /register?event=EVENT_ID to test</li>
        </ol>
      </div>
    </div>
  );
};

export default RegistrationTest;
